import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { IncomingCalls } from '../components/IncomingCalls';
import { MemeModal } from '../components/MemeModal';
import { MrryDirection, MrryMessageType, MrryModal } from '../components/MrryModal';
import { useSearch } from '../contexts/search';
import { AppState } from '../models/AppState';
import { styleSheet } from './styles';

/**
 * App-level chrome and side effects, rendered once above the router's `Switch`. These were bolted onto
 * `MainContainer` and so were coupled to whichever shell happened to be mounted; nothing here is layout.
 *
 * Reads `phoneCalls` off `AppState` rather than `useTelephony()` so it can sit above the `Switch` —
 * `TelephonyContextProvider` deliberately does not wrap the bare routes.
 */
function _CoffeeAppChrome() {
	const { setShowSearch } = useSearch();
	const userSession = useUserSession();
	const { phoneCalls } = AppState;

	const [showIncomingCalls, setShowIncomingCalls] = useState(false);

	useEffect(() => {
		const onGlobalKeydown = (e: KeyboardEvent) => {
			if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				setShowSearch(value => !value);
			}
		};
		window.addEventListener('keydown', onGlobalKeydown);
		return () => {
			window.removeEventListener('keydown', onGlobalKeydown);
		};
	}, [setShowSearch]);

	useEffect(() => {
		if (phoneCalls?.incomingCalls.length) {
			setShowIncomingCalls(true);
		} else {
			setShowIncomingCalls(false);
			phoneCalls?.clearIncomingCalls();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCalls?.incomingCalls.length]);

	const onIncomingCallsClose = () => {
		setShowIncomingCalls(false);
		phoneCalls?.clearIncomingCalls();
	};

	return (
		<>
			{!!showIncomingCalls && (
				<MrryModal
					fromDirection={MrryDirection.FromTop}
					message={{ content: <IncomingCalls />, type: MrryMessageType.Success }}
					onRequestClose={onIncomingCallsClose}
				/>
			)}
			<MemeModal />
			{userSession.account?.features?.aida?.enabled !== true && (
				<div className={css(styleSheet.noCoffee)}>
					This account does not have Coffee enabled. Please double check you are logged into the correct account.
				</div>
			)}
		</>
	);
}

export const CoffeeAppChrome = observer(_CoffeeAppChrome);
